import CancelRounded from '@mui/icons-material/CancelRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ChevronRight from '@mui/icons-material/ChevronRight';
import RemoveCircleRounded from '@mui/icons-material/RemoveCircleRounded';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type FC, type ReactNode, useMemo, useState } from 'react';
import type { RunConversationEvalCase, RunConversationEvalResponse } from 'services/types/conversationEvals';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  STATUS_ERROR,
  STATUS_GOOD,
  STATUS_WARN,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
} from 'styles/tokens';

type Outcome = 'success' | 'partial' | 'failure';

interface GradedCase {
  outcome: Outcome;
  index: number;
  data: RunConversationEvalCase;
}

interface EvalRunResultsProps {
  result: RunConversationEvalResponse;
  /** Flags a run graded against an unpublished template, so the number is not read as a baseline. */
  promptWasOverridden: boolean;
}

const OUTCOME: Record<Outcome, { color: string; label: string; icon: ReactNode }> = {
  success: { color: STATUS_GOOD, label: 'Pasó', icon: <CheckCircleRounded sx={{ fontSize: 17 }} /> },
  partial: { color: STATUS_WARN, label: 'Parcial', icon: <RemoveCircleRounded sx={{ fontSize: 17 }} /> },
  failure: { color: STATUS_ERROR, label: 'Falló', icon: <CancelRounded sx={{ fontSize: 17 }} /> },
};

const sectionLabel = {
  fontFamily: 'Jost, sans-serif',
  fontWeight: 700,
  fontSize: 11,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: alpha(NAVY, 0.5),
};

const bodyText = { fontFamily: 'Jost, sans-serif', fontSize: 13, color: NAVY };
const monoText = { fontFamily: 'ui-monospace, monospace', fontSize: 11.5, whiteSpace: 'pre-wrap' as const };

/** Per-case results arrive under `success_N` / `partial_N` / `failure_N` keys, not in an array. */
const readCases = (result: RunConversationEvalResponse): GradedCase[] =>
  Object.entries(result)
    .map(([key, value]) => {
      const match = /^(success|partial|failure)_(\d+)$/.exec(key);
      return match
        ? { outcome: match[1] as Outcome, index: Number(match[2]), data: value as RunConversationEvalCase }
        : null;
    })
    .filter((entry): entry is GradedCase => entry !== null)
    .sort((a, b) => a.index - b.index);

const describeToolCalls = (calls: { name?: string; arguments?: string }[] | undefined): string =>
  (calls ?? []).map((call) => `${call.name ?? '?'}(${call.arguments ?? ''})`).join('\n');

const answerText = (graded: GradedCase): string => {
  const { response } = graded.data;
  const calls = ((response?.toolCalls ?? []) as { function?: { name?: string; arguments?: string } }[]).map(
    (call) => call.function ?? {}
  );
  return [response?.completionMessageContent, describeToolCalls(calls)].filter(Boolean).join('\n') || 'Sin respuesta';
};

const DetailBlock: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography sx={{ ...sectionLabel, mb: 0.75 }}>{label}</Typography>
    <Box sx={{ bgcolor: SURFACE_CONTAINER_LOW, borderRadius: RADIUS_ITEM, p: 1.25 }}>{children}</Box>
  </Box>
);

const EvalRunResults: FC<EvalRunResultsProps> = ({ result, promptWasOverridden }) => {
  const cases = useMemo(() => readCases(result), [result]);
  const failedCount = cases.filter((item) => item.outcome !== 'success').length;
  // Default to the failures: with a large suite the passing cases are noise, and the whole point of a
  // run is what broke. Falls back to everything when the suite is green.
  const [filter, setFilter] = useState<'all' | 'failed'>(failedCount > 0 ? 'failed' : 'all');
  const [openCase, setOpenCase] = useState<GradedCase | null>(null);

  const visible = filter === 'failed' ? cases.filter((item) => item.outcome !== 'success') : cases;
  const successRate = Math.round((result.successRate ?? 0) * 100);

  const chipSx = (active: boolean) => ({
    fontFamily: 'Jost, sans-serif',
    fontWeight: 600,
    fontSize: 12,
    borderRadius: RADIUS_ITEM,
    bgcolor: active ? NAVY : alpha(NAVY, 0.06),
    color: active ? '#fff' : alpha(NAVY, 0.7),
    '&:hover': { bgcolor: active ? NAVY : alpha(NAVY, 0.1) },
  });

  return (
    <Box sx={{ bgcolor: SURFACE_CONTAINER_LOW, borderRadius: RADIUS_CARD, p: 2 }}>
      <Typography sx={{ ...sectionLabel, mb: 1 }}>Resultado</Typography>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: 34, color: NAVY }}>
          {successRate}%
        </Typography>
        <Typography sx={{ ...bodyText, color: alpha(NAVY, 0.55) }}>
          {result.successCount} de {result.total} casos
        </Typography>
      </Box>
      <Typography sx={{ ...bodyText, fontSize: 12, color: alpha(NAVY, 0.5), mb: 1.5 }}>
        Costo {(result.totalCosts ?? 0).toLocaleString('es-CL', { maximumFractionDigits: 2 })} CLP
        {promptWasOverridden ? ' · plantilla sin publicar' : ''}
      </Typography>

      {failedCount > 0 && (
        <Box sx={{ display: 'flex', gap: 0.75, mb: 1.25 }}>
          <Chip
            size="small"
            label={`Con problemas (${failedCount})`}
            onClick={() => setFilter('failed')}
            sx={chipSx(filter === 'failed')}
          />
          <Chip
            size="small"
            label={`Todos (${cases.length})`}
            onClick={() => setFilter('all')}
            sx={chipSx(filter === 'all')}
          />
        </Box>
      )}

      <Box sx={{ bgcolor: SURFACE_CONTAINER_LOWEST, borderRadius: RADIUS_ITEM, overflow: 'hidden' }}>
        {visible.map((item) => {
          const tone = OUTCOME[item.outcome];
          return (
            <Box
              key={`${item.outcome}-${item.index}`}
              onClick={() => setOpenCase(item)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 1,
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
                '&:hover': { bgcolor: alpha(NAVY, 0.04) },
              }}
            >
              <Box sx={{ color: tone.color, display: 'flex' }}>{tone.icon}</Box>
              <Typography sx={{ ...bodyText, fontSize: 12, color: alpha(NAVY, 0.45), minWidth: 46 }}>
                Caso {item.index}
              </Typography>
              <Typography
                sx={{
                  ...bodyText,
                  fontSize: 12.5,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.data.evalData?.expected?.responseDescription ?? 'Sin descripción'}
              </Typography>
              <ChevronRight sx={{ fontSize: 18, color: alpha(NAVY, 0.3) }} />
            </Box>
          );
        })}
      </Box>

      <Dialog
        open={!!openCase}
        onClose={() => setOpenCase(null)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              background: GLASS_BACKGROUND,
              backdropFilter: GLASS_BLUR,
              boxShadow: SHADOW,
              borderRadius: RADIUS_CARD,
              p: 2.5,
            },
          },
        }}
      >
        {openCase && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{ color: OUTCOME[openCase.outcome].color, display: 'flex' }}>
                {OUTCOME[openCase.outcome].icon}
              </Box>
              <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: 16, color: NAVY }}>
                Caso {openCase.index} · {OUTCOME[openCase.outcome].label}
              </Typography>
              {openCase.outcome === 'partial' && (
                <Typography sx={{ ...bodyText, fontSize: 12, color: alpha(NAVY, 0.5) }}>
                  {Math.round((openCase.data.score ?? 0) * 100)}% de las tools
                </Typography>
              )}
            </Box>

            <DetailBlock label="Qué esperabas">
              <Typography sx={{ ...bodyText }}>
                {openCase.data.evalData?.expected?.responseDescription ?? 'Sin descripción'}
              </Typography>
            </DetailBlock>

            {openCase.data.evalData?.expected?.expectedToolCalls && (
              <DetailBlock label="Tools esperadas">
                <Typography sx={{ ...monoText, color: NAVY }}>
                  {describeToolCalls(openCase.data.evalData.expected.expectedToolCalls)}
                </Typography>
              </DetailBlock>
            )}

            <DetailBlock label="Qué respondió">
              <Typography sx={{ ...bodyText, whiteSpace: 'pre-wrap' }}>{answerText(openCase)}</Typography>
            </DetailBlock>

            <Typography sx={{ ...bodyText, fontSize: 11.5, color: alpha(NAVY, 0.45) }}>
              {Math.round(openCase.data.completionTime ?? 0)} ms
              {openCase.data.alignmentScore !== undefined
                ? ` · juez ${openCase.data.alignmentScore}/5`
                : ' · sin juez (comparación literal de tools)'}
            </Typography>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default EvalRunResults;
